// ============================================================================
// merge-docs-by-identity.ts
// ----------------------------------------------------------------------------
// Converges the two legitimate Stage 1 document lanes:
//   1. a raw intake row that owns the source bytes / preview, and
//   2. a later extracted + classified row for that same source file.
//
// Generated document ids identify a ROW, not the physical source file. A raw
// row and its classified replacement can therefore carry different ids. The
// old id-first merge treated them as unrelated and rendered each PDF twice.
// Source identity is now explicit and structural; name alone is deliberately
// insufficient because two folders may contain genuinely different files with
// the same display name.
// ============================================================================

const SOURCE_ID_VERSION = 'qm-document-source-v1';
const transientFileIds = new WeakMap<object, string>();
let transientFileSequence = 0;

function nonBlank(value: any): string {
    return typeof value === 'string' && value.trim() ? value.trim() : '';
}

function isEmptyBinaryContainer(value: any): boolean {
    if (value === undefined || value === null || value === '') return true;
    if (typeof Blob !== 'undefined' && value instanceof Blob) return value.size === 0;
    if (value instanceof ArrayBuffer) return value.byteLength === 0;
    if (ArrayBuffer.isView(value)) return value.byteLength === 0;
    return false;
}

function finiteNonNegative(value: any): number | null {
    if (value === null || value === undefined || value === '' || typeof value === 'boolean') return null;
    const number = Number(value);
    return Number.isFinite(number) && number >= 0 ? number : null;
}

function normaliseSourcePath(value: any): string {
    const path = nonBlank(value).replace(/\\/g, '/').replace(/^\/+|\/+$/g, '');
    return path ? path.normalize('NFC') : '';
}

function sourceName(value: any): string {
    const name = nonBlank(value);
    return name ? name.normalize('NFC') : '';
}

function sourceByteSize(source: any): number | null {
    if (!source || typeof source !== 'object') return null;
    const candidates = [
        source.sourceFileSize,
        source.size,
        source.sizeBytes,
        source.originalSize,
        source.file?.size,
        source.pdfBlob?.size,
        source.pdfFile?.size,
        source.docBytes?.size,
        source.docBytes?.byteLength,
        source.bytes?.byteLength,
    ];
    for (const candidate of candidates) {
        const size = finiteNonNegative(candidate);
        if (size !== null) return size;
    }
    return null;
}

function sourceLastModified(source: any): number | null {
    if (!source || typeof source !== 'object') return null;
    const candidates = [
        source.sourceFileLastModified,
        source.lastModified,
        source.file?.lastModified,
        source.pdfBlob?.lastModified,
        source.pdfFile?.lastModified,
    ];
    for (const candidate of candidates) {
        const modified = finiteNonNegative(candidate);
        if (modified !== null) return modified;
    }
    return null;
}

function sourceDigest(source: any): string {
    if (!source || typeof source !== 'object') return '';
    return nonBlank(
        source.sourceFileSha256
        || source.sha256
        || source.contentHash
        || source.checksum,
    ).toLowerCase();
}

function sourceRelativePath(source: any): string {
    if (!source || typeof source !== 'object') return '';
    return normaliseSourcePath(
        source.sourceRelativePath
        || source.quoteMakerSourcePath
        || source.relativePath
        || source.webkitRelativePath
        || source.file?.webkitRelativePath,
    );
}

function isFileLikeSource(source: any): boolean {
    return !!source
        && typeof source === 'object'
        && typeof source.arrayBuffer === 'function'
        && !!sourceName(source.name || source.fileName);
}

/**
 * Give one selected browser File a session-unique identity. The WeakMap keeps
 * the identity stable even when a host File object is non-extensible; two
 * different File objects never converge merely because their visible tuple is
 * identical. File Explorer's durable quoteMakerFileId remains authoritative.
 */
export function ensureDocumentSourceFileId(source: any): string {
    if (!source || typeof source !== 'object') return '';
    const explicit = nonBlank(source.quoteMakerFileId);
    if (explicit) return explicit;
    if (!isFileLikeSource(source)) return '';
    const remembered = transientFileIds.get(source);
    if (remembered) return remembered;
    const uuid = globalThis.crypto?.randomUUID?.();
    const generated = `browser-file:${uuid || `${Date.now().toString(36)}-${(transientFileSequence += 1).toString(36)}`}`;
    transientFileIds.set(source, generated);
    try {
        Object.defineProperty(source, 'quoteMakerFileId', {
            value: generated,
            configurable: true,
            enumerable: false,
            writable: false,
        });
    } catch {
        // The WeakMap is the fallback for non-extensible host File objects.
    }
    return generated;
}

/**
 * Remove only duplicates that the browser/file-explorer evidence can prove are
 * the same selection. Object identity catches upstream fan-out of one File;
 * an explicit source id, path, or digest catches durable explorer records.
 * A coincidental name/size/timestamp tuple is intentionally not equality.
 */
export function dedupeSelectedFilesByIdentity(files: any): any[] {
    if (!Array.isArray(files)) return [];
    if (files.length <= 1) return files.filter(Boolean);
    const seenObjects = new Set<object>();
    const seenSourceIdentities = new Set<string>();
    const result: any[] = [];
    for (const file of files) {
        if (!file) continue;
        if (typeof file === 'object' && seenObjects.has(file)) continue;
        const sourceIdentity = documentSourceIdentity(file);
        if (sourceIdentity && seenSourceIdentities.has(sourceIdentity)) continue;
        if (typeof file === 'object') seenObjects.add(file);
        if (sourceIdentity) seenSourceIdentities.add(sourceIdentity);
        result.push(file);
    }
    return result;
}

/**
 * Return a deterministic physical-source identity when the record carries
 * enough evidence to distinguish same-name files safely.
 *
 * Filename alone and filename + rounded sizeKB are intentionally rejected.
 */
export function documentSourceIdentity(source: any): string {
    if (!source || typeof source !== 'object') return '';
    const explicit = nonBlank(source.sourceFileIdentity);
    if (explicit) return explicit;

    const fileMetadataId = nonBlank(source.fileMetadataId);
    if (fileMetadataId) {
        return `${SOURCE_ID_VERSION}:${JSON.stringify(['fileMetadataId', fileMetadataId])}`;
    }

    const quoteMakerFileId = nonBlank(source.quoteMakerFileId);
    // File Explorer ids are durable receipts and remain authoritative. IDs
    // minted for a plain browser File are session-only, however, so they must
    // not hide a subsequently available folder path or content digest.
    const durableQuoteMakerFileId = quoteMakerFileId && !quoteMakerFileId.startsWith('browser-file:')
        ? quoteMakerFileId
        : '';
    if (durableQuoteMakerFileId) {
        return `${SOURCE_ID_VERSION}:${JSON.stringify(['quoteMakerFileId', durableQuoteMakerFileId])}`;
    }

    const name = sourceName(source.sourceFileName || source.name || source.originalName || source.fileName || source.file?.name);
    const path = sourceRelativePath(source);
    const size = sourceByteSize(source);
    const modified = sourceLastModified(source);
    const digest = sourceDigest(source);

    // A non-leaf relative path is already the source's project-local address.
    // Include available version facts so raw/classified views converge while
    // two same-name files in different folders remain distinct.
    if (path && path !== name) {
        return `${SOURCE_ID_VERSION}:${JSON.stringify(['path', path, size, modified, digest])}`;
    }
    if (name && size !== null && digest) {
        return `${SOURCE_ID_VERSION}:${JSON.stringify(['digest', name, size, digest])}`;
    }
    if (quoteMakerFileId) {
        return `${SOURCE_ID_VERSION}:${JSON.stringify(['quoteMakerFileId', quoteMakerFileId])}`;
    }
    return '';
}

/**
 * Byte-free metadata stamped onto both raw intake and classified rows.
 */
export function buildDocumentSourceMetadata(source: any): Record<string, any> {
    if (!source || typeof source !== 'object') return {};
    // An explicit identity is already the persisted physical-source receipt.
    // Preserve it even if a malformed row also carries a conflicting qmid;
    // changing precedence during canonicalisation would make identity drift
    // across otherwise idempotent save/merge passes.
    const explicitIdentity = nonBlank(source.sourceFileIdentity);
    // Resolve every durable identity before minting a session-only browser id.
    // In particular, a folder-picked File's webkitRelativePath must converge
    // across separate selections of that same physical source.
    const existingSourceId = nonBlank(source.quoteMakerFileId);
    const hasPathIdentity = (() => {
        const name = sourceName(source.sourceFileName || source.name || source.originalName || source.fileName || source.file?.name);
        const path = sourceRelativePath(source);
        return !!path && path !== name;
    })();
    const hasDigestIdentity = !!sourceDigest(source) && sourceByteSize(source) !== null;
    const hasIndependentDurableIdentity = !!explicitIdentity
        || !!nonBlank(source.fileMetadataId)
        || hasPathIdentity
        || hasDigestIdentity;
    const durableIdentity = explicitIdentity || documentSourceIdentity(source);
    const sourceId = hasIndependentDurableIdentity && existingSourceId.startsWith('browser-file:')
        ? ''
        : (existingSourceId || (durableIdentity ? '' : ensureDocumentSourceFileId(source)));
    const identity = durableIdentity || (sourceId
        ? `${SOURCE_ID_VERSION}:${JSON.stringify(['quoteMakerFileId', sourceId])}`
        : '');
    if (!identity) return {};
    const name = sourceName(source.sourceFileName || source.name || source.originalName || source.fileName || source.file?.name);
    const size = sourceByteSize(source);
    const modified = sourceLastModified(source);
    const relativePath = sourceRelativePath(source);
    return {
        ...(sourceId ? { quoteMakerFileId: sourceId } : {}),
        ...(identity ? { sourceFileIdentity: identity } : {}),
        ...(name ? { sourceFileName: name } : {}),
        ...(size !== null ? { sourceFileSize: size } : {}),
        ...(modified !== null ? { sourceFileLastModified: modified } : {}),
        ...(relativePath ? { sourceRelativePath: relativePath } : {}),
    };
}

/** Row ids and source ids are parallel equality keys. */
export function documentIdentityKeys(doc: any): string[] {
    if (!doc || typeof doc !== 'object') return [];
    const keys: string[] = [];
    const fileMetadataId = nonBlank(doc.fileMetadataId);
    const id = nonBlank(doc.id);
    const sourceIdentity = documentSourceIdentity(doc);
    if (fileMetadataId) keys.push(`fileMetadataId:${fileMetadataId}`);
    if (id) keys.push(`id:${id}`);
    if (sourceIdentity) keys.push(`source:${sourceIdentity}`);
    return Array.from(new Set(keys));
}

/**
 * Backward-compatible primary identity accessor. Name-only identity was
 * removed because it merged distinct same-name documents.
 */
export function docIdentity(doc: any): string {
    if (!doc || typeof doc !== 'object') return '';
    return nonBlank(doc.fileMetadataId)
        || nonBlank(doc.id)
        || documentSourceIdentity(doc)
        || '';
}

/**
 * Upgrade a raw row in place with extraction/classification fields. Undefined
 * incoming values never erase durable bytes; nested metadata is enriched; and
 * the already-rendered row id remains stable.
 */
export function mergeDocumentRecords(base: any, incoming: any): any {
    if (!base || typeof base !== 'object') return incoming;
    if (!incoming || typeof incoming !== 'object') return base;
    const merged: Record<string, any> = { ...base };
    const durableSourceFields = new Set([
        'bytes', 'file', 'blob', 'pdfBlob', 'pdfFile', 'docBytes', 'arrayBuffer',
        'data', 'dataUrl', 'pdfDataUrl', 'docBlobId', 's3Key', 'pdfS3Key',
        'fileS3Key', 'cloudObjectKey', 'storageKey', 'objectKey', 'fileMetadataId',
    ]);
    const sourceIdentityFields = new Set([
        'fileMetadataId', 'quoteMakerFileId', 'sourceFileIdentity',
        'sourceRelativePath', 'quoteMakerSourcePath', 'relativePath', 'webkitRelativePath',
        'sourceFileSha256', 'sha256', 'contentHash', 'checksum',
    ]);
    const baseSourceIdentity = documentSourceIdentity(base);
    const incomingSourceIdentity = documentSourceIdentity(incoming);
    const directIdentityFields = [
        'sourceFileIdentity', 'fileMetadataId', 'quoteMakerFileId',
        'sourceFileSha256', 'sha256', 'contentHash', 'checksum',
    ];
    const hasDirectIdentityConflict = directIdentityFields.some((field) => {
        const digestField = ['sourceFileSha256', 'sha256', 'contentHash', 'checksum'].includes(field);
        const baseValue = digestField ? nonBlank(base[field]).toLowerCase() : nonBlank(base[field]);
        const incomingValue = digestField ? nonBlank(incoming[field]).toLowerCase() : nonBlank(incoming[field]);
        return !!baseValue && !!incomingValue && baseValue !== incomingValue;
    });
    const hasSourceIdentityConflict = hasDirectIdentityConflict || (!!baseSourceIdentity
        && !!incomingSourceIdentity
        && baseSourceIdentity !== incomingSourceIdentity);
    // Row ids are implementation details; a strong physical-source conflict
    // is evidence that these are different files. Never attach B's extracted
    // text/classification (or any of its alternate identity fields) to A's
    // retained bytes merely because a stale producer reused A's row id.
    if (hasSourceIdentityConflict) return base;
    for (const [key, value] of Object.entries(incoming)) {
        // A row id and a physical-source id are parallel keys. If a malformed
        // classified row matches the base by row id but claims a different
        // physical source, preserve the base identity. Otherwise one pass can
        // silently mutate source A into source B and the next save-time dedupe
        // collapses two real documents.
        if (sourceIdentityFields.has(key)
            && baseSourceIdentity
            && incomingSourceIdentity
            && baseSourceIdentity !== incomingSourceIdentity) continue;
        if (durableSourceFields.has(key)
            && isEmptyBinaryContainer(value)
            && !isEmptyBinaryContainer(base[key])) {
            continue;
        }
        if (value !== undefined) merged[key] = value;
    }
    if (base.metadata && typeof base.metadata === 'object'
        && incoming.metadata && typeof incoming.metadata === 'object') {
        merged.metadata = { ...base.metadata, ...incoming.metadata };
    }
    if (nonBlank(base.id)) merged.id = base.id;
    if (base.addedAt !== undefined) merged.addedAt = base.addedAt;
    return {
        ...merged,
        ...buildDocumentSourceMetadata(merged),
    };
}

function upsertDocument(result: any[], incoming: any): void {
    // Match one strongest identity only. An inconsistent incoming row may carry
    // row A's id and row B's source identity; it must never bridge and collapse
    // both existing documents. Key order is durable metadata id, stable row id,
    // then explicit physical-source identity.
    const incomingKeys = documentIdentityKeys(incoming);
    const incomingSourceIdentity = documentSourceIdentity(incoming);
    let targetIndex = -1;

    // Physical-source equality outranks a recycled row/file-metadata id. This
    // also makes a repeated conflicting import idempotent: once B was appended
    // beside A, the next B update finds B by source and enriches only B.
    if (incomingSourceIdentity) {
        const sourceMatches = result
            .map((existing, index) => documentSourceIdentity(existing) === incomingSourceIdentity ? index : -1)
            .filter(index => index >= 0);
        if (sourceMatches.length > 1) return;
        if (sourceMatches.length === 1) targetIndex = sourceMatches[0] ?? -1;
    }

    if (targetIndex < 0) {
        const rowKeys = incomingKeys.filter(key => !key.startsWith('source:'));
        const rowMatches = new Set<number>();
        for (const key of rowKeys) {
            const matchingIndex = result.findIndex(existing => documentIdentityKeys(existing).includes(key));
            if (matchingIndex >= 0) rowMatches.add(matchingIndex);
        }
        if (rowMatches.size > 1) return;
        if (rowMatches.size === 1) {
            const candidateIndex = [...rowMatches][0];
            if (candidateIndex === undefined) return;
            const candidateSourceIdentity = documentSourceIdentity(result[candidateIndex]);
            // A legacy row with no physical identity may be safely upgraded.
            // Two different strong identities may not be merged by row id.
            if (!incomingSourceIdentity || !candidateSourceIdentity
                || incomingSourceIdentity === candidateSourceIdentity) {
                targetIndex = candidateIndex;
            }
        }
    }
    if (targetIndex < 0) {
        // Canonicalise source metadata on the first append. Otherwise the
        // second identical merge changes the row shape and breaks idempotence.
        result.push({
            ...incoming,
            ...buildDocumentSourceMetadata(incoming),
        });
        return;
    }
    result[targetIndex] = mergeDocumentRecords(result[targetIndex], incoming);
}

/** Remove repeated identities while preserving first-seen order. */
export function dedupeDocsByIdentity(documents: any): any[] {
    const docs = Array.isArray(documents) ? documents : [];
    const result: any[] = [];
    for (const doc of docs) {
        if (!doc) continue;
        upsertDocument(result, doc);
    }
    return result;
}

/** Merge classified documents onto live raw/source state. */
export function mergeDocsByIdentity(base: any, incoming: any): any[] {
    const result = dedupeDocsByIdentity(base);
    const incomingArr = Array.isArray(incoming) ? incoming : [];
    for (const doc of incomingArr) {
        if (!doc) continue;
        upsertDocument(result, doc);
    }
    return result;
}
